import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadUser = useCallback(async () => {
    try {
      // Cookie is sent automatically — just call /me to check if we're authenticated
      const userData = await api.getCurrentUser();
      setUser(userData);
    } catch (err) {
      // 401 means no valid cookie — user is not signed in, that's fine
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    api.setUnauthorizedHandler(() => setUser(null));
    return () => api.setUnauthorizedHandler(null);
  }, []);

  const signIn = async (email, password) => {
    try {
      setError(null);
      const { user: userData } = await api.signIn(email, password);
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const signUp = async (email, password, name, acceptedTerms) => {
    try {
      setError(null);
      const { user: userData } = await api.signUp(email, password, name, acceptedTerms);
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const acceptTerms = async () => {
    const result = await api.acceptTerms();
    setUser(prev => ({ ...prev, ...result }));
    return result;
  };

  const signOut = async () => {
    try {
      await api.signOut(); // server clears the HttpOnly cookie
    } catch (err) {
      console.error('Sign out error:', err);
    } finally {
      setUser(null); // clear local state regardless
    }
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const value = {
    user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    acceptTerms,
    updateUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
